import PropTypes from 'prop-types'
import styled from 'styled-components'

import { base } from '../../mixins'
import { PB40T } from '../../colors'
import { getMargin, marginPropTypes } from '../../utils'

const weights = {
  normal: 400,
  bold: 700
}
const sizes = {
  default: {
    fontSize: '16px',
    lineHeight: '20px',
    margin: '0 0 20px 0'
  },
  extraSmall: {
    fontSize: '10px',
    lineHeight: '14px',
    margin: '0 0 14px 0'
  },
  small: {
    fontSize: '12px',
    lineHeight: '16px',
    margin: '0 0 16px 0'
  },
  medium: {
    fontSize: '14px',
    lineHeight: '18px',
    margin: '0 0 18px 0'
  },
  large: {
    fontSize: '20px',
    lineHeight: '24px',
    margin: '0 0 24px 0'
  }
}
const phrasingResets = {
  em: {
    'font-style': 'italic'
  },
  strong: {
    'font-weight': 'bolder'
  },
  small: {
    'font-size': '80%'
  },
  sub: {
    'font-size': '75%',
    'line-height': 0,
    position: 'relative',
    'vertical-align': 'baseline',
    bottom: '-0.25em'
  },
  sup: {
    'font-size': '75%',
    'line-height': 0,
    position: 'relative',
    'vertical-align': 'baseline',
    top: '-0.5em'
  }
}
const getBaseMargin = ({ size, as, my, mx, margin }) => {
  if (my || mx || margin) {
    return getMargin({ my, mx, margin })
  }

  if (as === 'p') {
    return `margin: ${sizes[size].margin};`
  }

  return 'margin: 0;'
}
const getResets = ({ as }) => phrasingResets[as]
const StyledText = styled.p`
  ${base};
  line-height: ${({ size }) => sizes[size].lineHeight};
  ${getBaseMargin};
  font-size: ${props => props.fontSize || sizes[props.size].fontSize};
  font-weight: ${props => weights[props.weight]};
  font-style: ${props => (props.isItalic ? 'italic' : 'normal')};
  text-align: ${props => props.align};
  text-transform: ${({ transform }) => transform};
  color: ${props => props.color};
  ${getResets};
  &:last-child {
    margin-bottom: 0;
  }
`
const tags = ['p', 'abbr', 'em', 'small', 'span', 'strong', 'sub', 'sup']
/**
 * The Text component can be used to display text with appropriate styling.
 */
const Text = ({
  children,
  fontSize,
  transform = 'none',
  isItalic = false,
  color = PB40T,
  as = 'p',
  weight = 'normal',
  size = 'default',
  align = 'left',
  ...rest
}) => {
  return (
    <StyledText
      align={align}
      size={size}
      isItalic={isItalic}
      weight={weight}
      fontSize={fontSize}
      transform={transform}
      as={as}
      color={color}
      {...rest}>
      {children}
    </StyledText>
  )
}

Text.propTypes = {
  ...marginPropTypes,
  /** The content between Text tags */
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(tags)]))
  ]).isRequired,
  /** @ignore */
  className: PropTypes.string,
  /** How to align the text */
  align: PropTypes.oneOf(['left', 'center', 'right']),
  /** Sets the text-transform */
  transform: PropTypes.oneOf(['uppercase', 'lowercase', 'capitalize', 'none']),
  /** The font size of the text */
  fontSize: PropTypes.string,
  /** Determines the relative size of the text (font-size) */
  size: PropTypes.oneOf(['default', 'extraSmall', 'small', 'medium', 'large']),
  /** Determines the font weight */
  weight: PropTypes.oneOf(['normal', 'bold']),
  /** Whether the font style is italic or normal */
  isItalic: PropTypes.bool,
  /** The HTML tag used to display the text */
  as: PropTypes.oneOf(tags),
  /** The color of the text */
  color: PropTypes.string
}

export { Text }
